import React from 'react';
import { fmtNum, TOOLTIP_W, TOOLTIP_H_EST } from './GtChartHelpers';

export interface GtTooltipRow {
    name: string;
    value: number;
    color: string;
}

interface GtChartTooltipProps {
    visible: boolean;
    cx: number;
    cy: number;
    catLabel: string;
    rows: GtTooltipRow[];
    isDark: boolean;
    containerW: number;
    height: number;
    isStacked?: boolean;
    stackGroups?: number[][];
    stackGroupLabels?: string[];
    formatTooltipValue?: (value: number, seriesName: string, catLabel: string) => string;
    /** Overrides the summed grand-total shown in the tooltip header chip. */
    totalOverride?: number | null;
}

export function GtChartTooltip({
    visible,
    cx,
    cy,
    catLabel,
    rows,
    isDark,
    containerW,
    height,
    isStacked = false,
    stackGroups,
    stackGroupLabels,
    formatTooltipValue,
    totalOverride,
}: GtChartTooltipProps) {
    if (!visible || rows.length === 0) return null;

    const grandTotal = totalOverride != null ? totalOverride : rows.reduce((s, r) => s + (r.value ?? 0), 0);

    // ── Smart placement ────────────────────────────────────────────────────────
    let tx = cx + 14;
    if (tx + TOOLTIP_W > containerW - 4) tx = cx - TOOLTIP_W - 10;
    if (tx < 4) tx = 4;
    let ty = cy - 12;
    if (ty < 4) ty = cy + 18;
    if (ty + TOOLTIP_H_EST > height + 20) ty = height - TOOLTIP_H_EST;

    // ── Design tokens ──────────────────────────────────────────────────────────
    const D = isDark;
    const bg = D ? '#0d1117' : '#ffffff';
    const border = D ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
    const divider = D ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
    const headTxt = D ? '#e2e8f0' : '#0f172a';
    const labelTxt = D ? '#7d8fa5' : '#64748b';
    const valueTxt = D ? '#f1f5f9' : '#0f172a';
    const mutedTxt = D ? '#3a4a5c' : '#c4cdd6';
    const secTxt = D ? '#3d5068' : '#a0b0c0';
    const totalChip = D ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)';

    const fmt = (row: GtTooltipRow) =>
        formatTooltipValue ? formatTooltipValue(row.value, row.name, catLabel) : fmtNum(row.value);

    // ── Section model ──────────────────────────────────────────────────────────
    type Section = { label?: string; indices: number[]; subtotal: number };
    const sections: Section[] = stackGroups?.length
        ? stackGroups.map((grp, gi) => ({
            label: stackGroupLabels?.[gi],
            indices: grp,
            subtotal: grp.reduce((s, i) => s + (rows[i]?.value ?? 0), 0),
        }))
        : [{ indices: rows.map((_, i) => i), subtotal: grandTotal }];

    const hasGroups = sections.some((s) => !!s.label);

    // ── Row renderer ──────────────────────────────────────────────────────────
    const renderRow = (row: GtTooltipRow, key: string, sectionTotal: number) => {
        const empty = row.value === 0;
        const pct = isStacked && !empty && grandTotal > 0 ? Math.round((row.value / grandTotal) * 100) : null;
        return (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Main row */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: empty ? 0.28 : 1,
                    minWidth: 0,
                }}>
                    {/* Square color swatch */}
                    <span style={{
                        width: 8, height: 8,
                        borderRadius: 2,
                        backgroundColor: row.color,
                        flexShrink: 0,
                    }} />
                    {/* Name — takes remaining space */}
                    <span style={{
                        flex: 1,
                        color: labelTxt,
                        fontSize: 10.5,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        letterSpacing: '0.005em',
                    }}>
                        {row.name}
                    </span>
                    {/* Percentage — tinted with series color */}
                    {pct !== null && (
                        <span style={{
                            color: row.color,
                            fontSize: 9.5,
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            flexShrink: 0,
                            opacity: 0.72,
                            letterSpacing: '0.005em',
                        }}>
                            {pct}%
                        </span>
                    )}
                    {/* Value — right-aligned */}
                    <span style={{
                        color: empty ? mutedTxt : valueTxt,
                        fontWeight: 700,
                        fontSize: 11,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                        flexShrink: 0,
                    }}>
                        {empty ? '\u2014' : fmt(row)}
                    </span>
                </div>
                {/* Mini progress bar — visualises proportion within section */}
                {!empty && pct !== null && (
                    <div style={{
                        height: 2.5,
                        borderRadius: 99,
                        background: D ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
                        overflow: 'hidden',
                        marginLeft: 16,
                    }}>
                        <div style={{
                            height: '100%',
                            width: `${pct}%`,
                            background: row.color,
                            borderRadius: 99,
                            opacity: 0.8,
                        }} />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{
            position: 'absolute',
            left: tx,
            top: ty,
            zIndex: 20,
            pointerEvents: 'none',
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 10,
            overflow: 'hidden',
            boxShadow: D
                ? '0 20px 48px rgba(0,0,0,0.72), 0 0 0 0.5px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.04)'
                : '0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
            minWidth: TOOLTIP_W,
            maxWidth: TOOLTIP_W + 24,
            fontFamily: 'inherit',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
        }}>

            {/* ── HEADER ─────────────────────────────────────────────────────── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 10px 5px',
                borderBottom: `1px solid ${divider}`,
                gap: 8,
            }}>
                {/* Date / category label */}
                <span style={{
                    color: headTxt,
                    fontWeight: 600,
                    fontSize: 10.5,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    opacity: 0.7,
                }}>
                    {catLabel}
                </span>

                {/* Grand total chip — only for stacked charts */}
                {grandTotal > 0 && (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 3,
                        flexShrink: 0,
                    }}>
                        <span style={{
                            fontSize: 8,
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: labelTxt,
                            opacity: 0.7,
                        }}>
                            Total
                        </span>
                        <span style={{
                            color: valueTxt,
                            fontWeight: 750,
                            fontSize: 12,
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '-0.03em',
                        }}>
                            {fmtNum(grandTotal)}
                        </span>
                    </span>
                )}
            </div>

            {/* ── BODY ───────────────────────────────────────────────────────── */}
            <div style={{
                padding: hasGroups ? '6px 10px 8px' : '6px 10px 7px',
                display: 'flex',
                flexDirection: 'column',
                gap: hasGroups ? 10 : 6,
            }}>
                {sections.map((sec, si) => {
                    const accentColor = sec.indices[0] !== undefined ? rows[sec.indices[0]]?.color : secTxt;
                    return (
                        <div key={si}>
                            {/* Group label row */}
                            {sec.label && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 6,
                                }}>
                                    <span style={{
                                        fontSize: 9,
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: accentColor,
                                        borderLeft: `2px solid ${accentColor}`,
                                        paddingLeft: 5,
                                        lineHeight: 1,
                                        opacity: 0.85,
                                    }}>
                                        {sec.label}
                                    </span>
                                    {isStacked && sec.subtotal > 0 && (
                                        <span style={{
                                            fontSize: 10.5,
                                            fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: valueTxt,
                                            letterSpacing: '-0.02em',
                                        }}>
                                            {fmtNum(sec.subtotal)}
                                        </span>
                                    )}
                                </div>
                            )}
                            {/* Data rows */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {sec.indices.map((idx) =>
                                    rows[idx] ? renderRow(rows[idx], `${si}-${idx}`, sec.subtotal) : null
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


